import { TextField, Typography, Grid, InputAdornment, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";
import {
  Create,
  ReferenceInput,
  SimpleForm,
  Button,
  required,
  useNotify,
  TextInput,
  SelectInput,
  ArrayInput,
  SimpleFormIterator,
  NumberInput,
  useStore,
} from "react-admin";
import dataProvider from "server/dataProvider";
import { AdminExamsPaymentMethodChoices, GenderOptionsId } from "utils/choices";
import { CampaignSubTypes } from "utils/constants";
import { settings } from "app";
import { candidateRequests } from "api/candidateApi";
import { accountRequests } from "api/accountApi";
import { useNotifyError } from "utils/useNotifyError";

const CreatePaidCandidate = ({ props, resource }) => {
  const notifyError = useNotifyError();
  const notify = useNotify();
  const [userData, setData] = useState(null);
  const [personId, setPersonId] = useState(null);
  // Read-only consumer of the committed campaign from the paid candidates list.
  const [filters] = useStore(`campaign-query-subtype-${CampaignSubTypes.Paid}`, null);
  const campaignId = filters?.campaignId ?? null;

  const searchUserByPersonId = async () => {
    if (!personId) {
      notify("Моля въведете ЕГН!", { type: "error" });
    } else if (!personId.match(/^[0-9]{10}$/)) {
      notify("Моля въведете валидно ЕГН!", { type: "error" });
    } else {
      try {
        const { data } = await dataProvider.customRequest(
          accountRequests.getUsersInfoSearch({ personId }),
          null,
          "GET",
        );

        if (Object.entries(data).length > 0) {
          const { userId, citizenship, ...rest } = data;

          setData(prevData => ({
            ...prevData,
            userId,
            campaignId: Number(campaignId),
            info: {
              ...(prevData?.info || {}),
              personId: personId,
              citizenshipId: 1,
              residenceCountryId: 22,
              ...rest,
            },
          }));
        }
      } catch (error) {
        notifyError(error);
        setData(null);
      }
    }
  };
  const sendData = data => {
    const merged = {
      ...userData,
      ...data,
      info: {
        ...(userData?.info || {}),
        ...(data?.info || {}),
      },
    };
    return merged;
  };
  return (
    <>
      <Grid
        container
        spacing={4}
        sx={{
          justifyContent: "center",
          width: "fit-content",
          marginX: "auto",
          marginY: "2rem",
          padding: "1rem 1rem 4rem 1rem",
        }}
      >
        <Grid
          item
          xs={12}
        >
          <Typography
            sx={{ textAlign: "center" }}
            color={"darkRed"}
            fontSize={24}
            paddingY={2}
            fontWeight={"bold"}
          >
            Въведете ЕГН
          </Typography>
        </Grid>
        <Grid
          item
          xs={7}
        >
          <TextField
            variant="outlined"
            sx={{ margin: "auto" }}
            label="ЕГН"
            name="personId"
            fullWidth
            value={personId}
            onChange={e => {
              const id = e.target.value;

              if (!id) {
                setPersonId(null);
              } else {
                setPersonId(id);
              }
            }}
            InputProps={{
              endAdornment: personId && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => {
                      setPersonId("");
                      setData(null);
                    }}
                    edge="end"
                    size="small"
                  >
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid
          item
          xs={2}
        >
          <Button
            label="Провери"
            sx={{ margin: "auto" }}
            onClick={searchUserByPersonId}
            size="large"
            variant="contained"
          />
        </Grid>
      </Grid>

      {userData && (
        <Create
          {...props}
          title={`Създаване на кандидатура за платено обучение с ЕГН: ${personId}`}
          transform={sendData}
        >
          <SimpleForm>
            <Typography
              color={"primary"}
              fontSize={18}
              paddingY={2}
            >
              Лични данни
            </Typography>

            <Grid
              container
              spacing={2}
            >
              <Grid
                item
                xs={12}
                md={4}
              >
                <TextInput
                  source="info.firstName"
                  label="Име"
                  defaultValue={userData?.info?.firstName}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
              >
                <TextInput
                  source="info.secondName"
                  label="Презиме"
                  defaultValue={userData?.info?.secondName || ""}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
              >
                <TextInput
                  source="info.lastName"
                  label="Фамилия"
                  defaultValue={userData?.info?.lastName || ""}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={4}
              >
                <TextInput
                  label="Телефон"
                  source="info.contactPhone"
                  defaultValue={userData?.info?.contactPhone}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
              >
                <TextInput
                  source="email"
                  label="Имейл"
                  defaultValue={userData?.info?.email || ""}
                  validate={required()}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
              >
                <SelectInput
                  label="Пол"
                  source="info.gender"
                  choices={GenderOptionsId}
                  validate={[required()]}
                  fullWidth
                  defaultValue={userData?.info?.gender}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={4}
                sx={{ marginTop: "-2rem" }}
              >
                <ArrayInput
                  source="specialities"
                  label=" "
                  defaultValue={[{}]}
                >
                  <SimpleFormIterator
                    inline
                    disableReordering
                    disableAdd
                    disableRemove
                    sx={settings.style.iteratorNone}
                  >
                    <ReferenceInput
                      source="specialityId"
                      reference={candidateRequests.getCampaignSpecialitiesSelect(campaignId)}
                    >
                      <SelectInput
                        label="Специалност"
                        optionText="nameBg"
                        validate={[required()]}
                      />
                    </ReferenceInput>
                    <NumberInput
                      sx={{ display: "none" }}
                      source="priority"
                      label="Приоритет"
                      defaultValue={1}
                    />
                    <NumberInput
                      sx={{ display: "none" }}
                      source="studyTypeId"
                      label=" "
                      defaultValue={2}
                    />
                  </SimpleFormIterator>
                </ArrayInput>
              </Grid>

              <Grid
                item
                xs={12}
                md={4}
              >
                <SelectInput
                  label="Платена такса за изпит"
                  source="isPaidExams"
                  choices={AdminExamsPaymentMethodChoices}
                  validate={required()}
                />
              </Grid>
            </Grid>
          </SimpleForm>
        </Create>
      )}
    </>
  );
};
export default CreatePaidCandidate;
